import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { pode } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialResult, PsychosocialClassification } from '../../types/psychosocial';

const classificationLabels: Record<PsychosocialClassification, string> = {
  green: 'Verde',
  yellow: 'Amarelo',
  red: 'Vermelho',
};

const classificationColors: Record<PsychosocialClassification, 'success' | 'warning' | 'error'> = {
  green: 'success',
  yellow: 'warning',
  red: 'error',
};

// Get general classification (predominant)
const getGeneralClassification = (scores: PsychosocialResult['scores']): PsychosocialClassification => {
  const counts: Record<string, number> = { green: 0, yellow: 0, red: 0 };
  scores.forEach((s) => { counts[s.classification] = (counts[s.classification] || 0) + 1; });
  if (counts['red']! >= counts['yellow']! && counts['red']! >= counts['green']!) return 'red';
  if (counts['yellow']! >= counts['green']!) return 'yellow';
  return 'green';
};

const columns: DataTableColumn<PsychosocialResult>[] = [
  { id: 'employeeName', label: 'Funcionário', primary: true, render: (r) => r.employeeName },
  { id: 'department', label: 'Setor', render: (r) => r.department || '-' },
  { id: 'occupation', label: 'Cargo', render: (r) => r.occupation || '-' },
  {
    id: 'completedAt',
    label: 'Data',
    render: (r) => new Date(r.completedAt).toLocaleDateString('pt-BR'),
  },
  {
    id: 'general',
    label: 'Classificação Geral',
    align: 'center',
    render: (r) => {
      const general = getGeneralClassification(r.scores);
      return (
        <Chip
          size="small"
          label={classificationLabels[general]}
          color={classificationColors[general]}
        />
      );
    },
  },
  {
    id: 'atRisk',
    label: 'Dimensões em Risco',
    align: 'center',
    render: (r) => {
      const redCount = r.scores.filter((s) => s.classification === 'red').length;
      return redCount > 0 ? (
        <Chip size="small" label={`${redCount} em risco`} color="error" variant="outlined" />
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      );
    },
  },
];

const PsychosocialResultsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const individual = useVeredito(
    '/psychosocial/campaigns/:id/results',
    pode('psychosocial_response.view_individual')
  );

  const [results, setResults] = useState<PsychosocialResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const loadResults = useCallback(async () => {
    setLoading(true);
    try {
      const result = await psychosocialService.getResults(Number(id), {
        page: page + 1,
        limit: rowsPerPage,
      });
      setResults(result.data);
      setTotalCount(result.pagination?.total || 0);
    } catch {
      enqueueSnackbar('Erro ao carregar resultados', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const handleExport = async () => {
    try {
      const blob = await psychosocialService.exportExcel(Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resultados-psicossocial-${id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Erro ao exportar', { variant: 'error' });
    }
  };

  if (!individual.liberado) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Psicossocial', path: '/psychosocial' },
          { label: 'Campanha', path: `/psychosocial/campaigns/${id}` },
          { label: 'Resultados' },
        ]}
        title="Resultados Individuais"
      >
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/psychosocial/campaigns/${id}/dashboard`)}>
              Ir para Dashboard
            </Button>
          }
        >
          {individual.motivo === 'permissao'
            ? 'Você não tem permissão para visualizar resultados individuais. Apenas profissionais do SESMT podem acessar esta página.'
            : individual.explicacao}
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Psicossocial', path: '/psychosocial' },
        { label: 'Campanha', path: `/psychosocial/campaigns/${id}` },
        { label: 'Resultados' },
      ]}
      title="Resultados Individuais"
      subtitle="Resultados individuais dos funcionários na campanha"
      actions={
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}>
          Exportar Excel
        </Button>
      }
    >
      <DataTable
        columns={columns}
        rows={results}
        getRowId={(r) => r.employeeId}
        loading={loading}
        emptyState={{
          title: 'Nenhum resultado',
          description:
            'Os resultados vêm das respostas que os funcionários enviam nas campanhas psicossociais.',
        }}
        pagination={{
          page,
          rowsPerPage,
          totalCount,
          onPageChange: setPage,
          onRowsPerPageChange: (n) => { setRowsPerPage(n); setPage(0); },
          rowsPerPageOptions: [10, 20, 50],
        }}
      />
    </PageShell>
  );
};

export default PsychosocialResultsPage;
